import { ArrowLeftRight, BookOpen, Cigarette, GraduationCap, HeartHandshake, Languages, MapPin, Ruler, Wine } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { MatchProfile, Profile } from "../../types";
import { formatLocationLine } from "./locationUtils";
import { formatHeight } from "./heightUtils";
import { useHeightDisplayUnit } from "./useHeightDisplayUnit";

type DetailRow = {
  key: string;
  icon: LucideIcon;
  label: string;
  value: string;
  toggle?: {
    ariaLabel: string;
    onToggle: () => void;
    value: string;
  };
};

export function ProfileDetails({ profile }: { profile: MatchProfile | Profile }) {
  const { useImperial, toggle } = useHeightDisplayUnit();
  const languageLevel = (level: NonNullable<Profile["languages"]>[number]["level"]) => {
    if (level === "fluent") return "Fluent";
    if (level === "conversational" || level === "spoken_written") return "Conversational";
    if (level === "spoken_only") return "Spoken";
    if (level === "written_only") return "Written";
    return "Basic";
  };

  const rows: DetailRow[] = [
    {
      key: "location",
      icon: MapPin,
      label: "Location",
      value: formatLocationLine(profile.city, profile.state, profile.country),
    },
    profile.height && {
      key: "height",
      icon: Ruler,
      label: "Height",
      value: formatHeight(profile.height, useImperial),
      toggle: {
        ariaLabel: useImperial ? "Show height in centimeters" : "Show height in feet and inches",
        onToggle: toggle,
        value: formatHeight(profile.height, useImperial),
      },
    },
    profile.educationLevel && {
      key: "education",
      icon: GraduationCap,
      label: "Education",
      value: profile.educationLevel.replace(/_/g, " "),
    },
    profile.languages?.length && {
      key: "languages",
      icon: Languages,
      label: "Languages",
      value: profile.languages
        .map(({ language, level }) => `${language} (${languageLevel(level)})`)
        .join(", "),
    },
    {
      key: "religion",
      icon: BookOpen,
      label: "Religion",
      value: profile.religion.replace("interested:", "Interested - "),
    },
    {
      key: "status",
      icon: HeartHandshake,
      label: "Status",
      value: profile.maritalHistory.replace(/_/g, " "),
    },
    {
      key: "smoking",
      icon: Cigarette,
      label: "Smoking",
      value: profile.smoking.replace(/_/g, " "),
    },
    {
      key: "drinking",
      icon: Wine,
      label: "Drinking",
      value: profile.drinking.replace(/_/g, " "),
    },
  ].filter(Boolean) as DetailRow[];

  return (
    <div className="details">
      {rows.map(({ key, icon: Icon, label, value, toggle: rowToggle }) => (
        <div className="detail-row" key={key}>
          <Icon size={16} />
          <span>
            <strong>{label}</strong>
            {rowToggle ? (
              <button
                className="detail-row-value-toggle"
                type="button"
                aria-label={rowToggle.ariaLabel}
                title={rowToggle.ariaLabel}
                onClick={rowToggle.onToggle}
              >
                <span className="detail-row-value-toggle-text">{rowToggle.value}</span>
                <ArrowLeftRight className="detail-row-value-toggle-icon" size={13} aria-hidden="true" />
              </button>
            ) : (
              value
            )}
          </span>
        </div>
      ))}
      {!!profile.interests?.length && <div className="tag-row">{profile.interests.map((item) => <span key={item}>{item}</span>)}</div>}
    </div>
  );
}
